/**
 * ActivitiesFilters - Barra de filtros do feed de atividades
 * 
 * Utiliza os componentes de filtro existentes do CRM:
 * - CrmPeriodFilter: Período (datas)
 * - CrmWorkspaceFilter: Workspace do negócio
 * - CrmSellerFilter: Vendedor do negócio
 * - CrmUserFilter: Responsável pela atividade (performed_by)
 * - CrmActivityStatusFilter: Status (Todos/Aberto/Concluído)
 * 
 * @version 2.0.0
 * RELEASE: 2026-02-09 v1.0.0 - Implementação inicial
 * RELEASE: 2026-02-09 v2.0.0 - Reescrita com 5 filtros padrão CRM
 */
import React from 'react';
import { Calendar, X } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { 
  CrmPeriodFilter, 
  CrmWorkspaceFilter, 
  CrmSellerFilter,
  CrmUserFilter,
  CrmActivityStatusFilter,
  CrmSchedulingTypeFilter,
  CrmEventTypeFilter,
  CrmActivityOriginFilter,
} from '@/components/crm/filters';
import type { SchedulingTypeFilter } from '@/components/crm/filters/CrmSchedulingTypeFilter';
import type { ActivityOriginFilter } from '@/components/crm/filters/CrmActivityOriginFilter';
import { calculatePeriodDates, type PeriodFilter, type PeriodPreset } from '@/hooks/useCrmOverviewFilters';
import type { ActivityStatusFilter } from '@/components/crm/filters/CrmActivityStatusFilter';
import type { ActivityType } from '@/hooks/useCrmDealActivities';

/**
 * Interface dos filtros de atividades
 */
export interface ActivityFilters {
  /** Filtro de período (datas) */
  period: PeriodFilter;
  /** Workspace do negócio (null = todos) */
  workspaceId: string | null;
  /** Vendedor do negócio (null = todos) */
  sellerId: string | null;
  /** Responsável pela atividade (null = todos) */
  performedByUserId: string | null;
  /** Status: all, open, completed */
  status: ActivityStatusFilter;
  /** Tipo de agendamento: null (todos), online, meeting, task */
  schedulingType?: SchedulingTypeFilter;
  /** ID do tipo de evento (null = todos) - v1.7.0 */
  eventTypeId?: string | null;
  /** Origem da atividade: all, manual, system - v1.7.2 */
  origin?: ActivityOriginFilter;
  /** Tipo de atividade (controlado pelas abas) */
  activityType?: ActivityType;
  /** Somente com comentários (controlado pela aba — legado client-side) */
  onlyWithComments?: boolean;
  /** v1.10.0: Somente atividades em que o usuário foi mencionado (server-side, aba Comentários) */
  onlyWithMyMentions?: boolean;
  /** Data específica do calendário */
  selectedDate?: Date;
}

/**
 * Props do componente
 */
interface ActivitiesFiltersProps {
  filters: ActivityFilters;
  onFiltersChange: (filters: ActivityFilters) => void;
  /** Exibir filtro de tipo de agendamento (default: false, visível apenas no Calendário) */
  showSchedulingTypeFilter?: boolean;
}

/**
 * Componente de filtros do feed de atividades
 * Reutiliza os filtros visuais do Dashboard CRM
 */
export function ActivitiesFilters({ filters, onFiltersChange, showSchedulingTypeFilter = false }: ActivitiesFiltersProps) {
  // Handler para mudança de preset do período
  // Handler para mudança de preset do período - recalcula datas para atualizar queryKey
  const handlePeriodPresetChange = (preset: PeriodPreset) => {
    const { startDate, endDate } = calculatePeriodDates(preset);
    onFiltersChange({
      ...filters,
      period: { preset, startDate, endDate },
    });
  };

  // Handler para período customizado
  const handlePeriodCustomChange = (startDate: Date, endDate: Date) => {
    onFiltersChange({
      ...filters,
      period: { preset: 'custom', startDate, endDate },
    });
  };

  // Handler para limpar data selecionada do calendário
  const handleClearSelectedDate = () => {
    onFiltersChange({
      ...filters,
      selectedDate: undefined,
    });
  };

  return (
    <div className="flex items-center gap-1.5 flex-nowrap overflow-x-auto scrollbar-none">
      {/* Filtro de Período - Compacto */}
      <CrmPeriodFilter
        value={filters.period}
        onPresetChange={handlePeriodPresetChange}
        onCustomChange={handlePeriodCustomChange}
        className="h-[22px] text-xs px-2"
      />

      {/* Filtro de Workspace - Compacto */}
      <CrmWorkspaceFilter
        value={filters.workspaceId}
        onChange={(workspaceId) => onFiltersChange({ ...filters, workspaceId })}
        className="h-[22px] text-xs px-2"
      />

      {/* Filtro de Vendedor (do negócio) - Compacto */}
      <CrmSellerFilter
        value={filters.sellerId}
        onChange={(sellerId) => onFiltersChange({ ...filters, sellerId })}
        className="h-[22px] text-xs px-2"
      />

      {/* Filtro de Responsável (quem executou a atividade) - Compacto */}
      <CrmUserFilter
        value={filters.performedByUserId}
        onChange={(performedByUserId) => onFiltersChange({ ...filters, performedByUserId })}
        className="h-[22px] text-xs px-2"
      />

      {/* Filtro de Status (Aberto/Concluído) - Compacto */}
      <CrmActivityStatusFilter
        value={filters.status}
        onChange={(status) => onFiltersChange({ ...filters, status })}
        className="h-[22px] text-xs px-2"
      />

      {/* Filtro de Origem (Manual/Sistema) - v1.7.2 */}
      <CrmActivityOriginFilter
        value={filters.origin ?? 'all'}
        onChange={(origin) => onFiltersChange({ ...filters, origin })}
        className="h-[22px] text-xs px-2"
      />

      {/* Filtro de Tipo de Agendamento - Visível apenas na página de Agendamento */}
      {showSchedulingTypeFilter && (
        <CrmSchedulingTypeFilter
          value={filters.schedulingType ?? null}
          onChange={(schedulingType) => onFiltersChange({ ...filters, schedulingType })}
          className="h-[22px] text-xs px-2"
        />
      )}

      {/* Filtro de Tipo de Evento - Visível apenas na página de Agendamento (v1.7.0) */}
      {showSchedulingTypeFilter && (
        <CrmEventTypeFilter
          value={filters.eventTypeId ?? null}
          onChange={(eventTypeId) => onFiltersChange({ ...filters, eventTypeId })}
          className="h-[22px] text-xs px-2"
        />
      )}

      {/* Badge de data selecionada (do calendário lateral) */}
      {filters.selectedDate && (
        <Badge 
          variant="secondary" 
          className="h-9 px-3 gap-1.5 cursor-pointer hover:bg-destructive/10"
          onClick={handleClearSelectedDate}
        >
          <Calendar className="h-3.5 w-3.5" />
          {filters.selectedDate.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })}
          <X className="h-3 w-3 ml-1" />
        </Badge>
      )}
    </div>
  );
}
